/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'tfc-components';

import { ReactComponent as Empty } from 'assets/icons/ic_empty_chat.svg';
import { ReactComponent as ErrorSadFace } from 'assets/icons/ic_error_face.svg';
import { ReactComponent as Retry } from 'assets/icons/ic_white_retry.svg';
import Icon from 'components/atoms/Icon';
import LoadingSpinner from 'components/atoms/LoadingSpinner';
import { useToast } from 'components/molecules/Toast';
import HeaderChatbot from 'components/organisms/HeaderChatbot';
import ConversationList from 'components/templates/ConversationList';
import { ContextChatbot } from 'components/templates/MainlayoutChatbot';
import {
  createConversationChatBotService, getAccountConversationsFavoriteService,
  getAccountConversationsService, updateAccountConversationFavoriteService
} from 'services/accountConversations';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers, { getErrors } from 'utils/functions';

const PAGE_LIMIT = 20;

const ConversationHistory: React.FC<{ codeConversation?: string }> = ({ codeConversation }) => {
  const navigate = useNavigate();
  const infoChatbot = useContext(ContextChatbot);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const {
    data: conversationData,
    isFetchingNextPage: loadingData,
    isLoading: loadingDataList,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch: refetchConversation
  } = useInfiniteQuery({
    queryKey: ['getAccountConversationQuery'],
    queryFn: ({ pageParam = 1 }) => getAccountConversationsService({
      limit: PAGE_LIMIT,
      page: pageParam,
    }),
    getNextPageParam:
      (lastPage) => (lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : null),
    onError: () => {
      showToast({
        message: t('chatbot.loadConversationError'),
        show: true,
        type: 'error',
      });
    },
  });

  const {
    data: conversationFavoriteData,
    isFetchingNextPage: loadingFavoriteData,
    isLoading: loadingFavoriteDataList,
    hasNextPage: hasFavoriteNextPage,
    fetchNextPage: fetchFavoriteNextPage,
    isError: isErrorFavorite,
    refetch: refetchConversationFavorite
  } = useInfiniteQuery({
    queryKey: ['getAccountConversationFavoriteQuery'],
    queryFn: ({ pageParam = 1 }) => getAccountConversationsFavoriteService({
      limit: PAGE_LIMIT,
      page: pageParam,
    }),
    getNextPageParam:
      (lastPage) => (lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : null),
    onError: () => {
      showToast({
        message: t('chatbot.loadConversationError'),
        show: true,
        type: 'error',
      });
    },
  });

  const handleLoadMoreConversation = () => {
    if (hasFavoriteNextPage) {
      fetchFavoriteNextPage();
    } else if (hasNextPage && !hasFavoriteNextPage) {
      fetchNextPage();
    }
  };

  const { mutate: createConversation, isLoading: isLoadingNewConversation } = useMutation(
    ['create-conversation'],
    () => createConversationChatBotService({}),
    {
      onError: (err) => {
        showToast({
          message: getErrors(err),
          show: true,
          type: 'error',
        });
      },
      onSuccess: (data) => {
        console.log('history');
        navigate(`${ROUTE_PATHS.CHATBOT_WIDGET}?code=${data.code}`);
      },
    }
  );

  const {
    mutate: handleToggleFavorite,
  } = useMutation(['toggle-favorite'], updateAccountConversationFavoriteService, {
    onSuccess() {
      refetchConversation();
      refetchConversationFavorite();
    },
  });

  const handleToggleFavoriteConversation = (codeElement: string, favorite: boolean) => {
    handleToggleFavorite({ code: codeElement, favorite });
  };

  const conversations = useMemo(() => {
    if (conversationData?.pages) {
      return conversationData?.pages?.map((page) => page.data).flat();
    }
    return [];
  }, [conversationData]);

  const conversationsFavorite = useMemo(() => {
    if (conversationFavoriteData?.pages) {
      return conversationFavoriteData?.pages?.map((page) => page.data).flat();
    }
    return [];
  }, [conversationFavoriteData]);

  if (loadingDataList || loadingFavoriteDataList) {
    return <LoadingSpinner variant="fullscreen" width={32} color={infoChatbot.mainColor} />;
  }

  if (isError || isErrorFavorite) {
    return (
      <>
        <HeaderChatbot isBackButton title={infoChatbot?.name || ''} handleBack={() => navigate(-1)} />
        <div className="p-conversationHistory-empty_wrapper">
          <div className="p-conversationHistory-empty">
            <div className="p-conversationHistory_image">
              <ErrorSadFace className="p-conversationHistory_image_error" />
            </div>
            <Typography.Heading extendClasses="p-conversationHistory_heading" fontweight="600">
              Oops!!!
            </Typography.Heading>
            <Typography.Text extendClasses="p-conversationHistory_desc">
              {t('chatbot.loadConversationError')}
            </Typography.Text>
            <button
              type="button"
              className={mapModifiers('p-conversationHistory_footer_newBtn', loadingDataList || loadingFavoriteDataList ? 'disabled' : '')}
              onClick={() => queryClient.invalidateQueries(['getAccountConversationQuery'])}
              disabled={loadingDataList || loadingFavoriteDataList}
            >
              {loadingDataList || loadingFavoriteDataList ? (
                <LoadingSpinner width={24} color="#fff" />
              ) : (
                <>
                  <Retry className="p-conversationHistory_footer_newBtn_icon" />
                  Thử lại
                </>
              )}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="p-conversationHistory-empty_wrapper">
        <div className="p-conversationHistory-empty">
          <div className="p-conversationHistory_image">
            <Empty className="p-conversationHistory_image_empty" />
          </div>
          <Typography.Heading extendClasses="p-conversationHistory_heading" fontweight="600">
            {t('chatbot.emptyConversation')}
          </Typography.Heading>
          <Typography.Text extendClasses="p-conversationHistory_desc">
            {t('chatbot.startConversation')}
          </Typography.Text>
          <button
            type="button"
            className="p-conversationHistory_footer_newBtn"
            onClick={() => {
              navigate(`${ROUTE_PATHS.CHATBOT_WIDGET}?mode=new`);
            }}
          >
            <Icon iconName="whitePlus" size="20x20" className="p-conversationHistory_footer_newBtn_icon" />
            {t('chatbot.newConversation')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-conversationHistory">
      <ConversationList
        dataList={conversations || []}
        dataListFavorite={conversationsFavorite || []}
        loadingData={loadingFavoriteData || loadingData}
        handleLoadMoreConversation={handleLoadMoreConversation}
        handleNewConversation={() => createConversation()}
        loadingCreate={isLoadingNewConversation}
        handleToggleFavorite={handleToggleFavoriteConversation}
        codeConversation={codeConversation}
      />
      <div className="p-conversationHistory_footer">
        <button
          type="button"
          className="p-conversationHistory_footer_newBtn"
          onClick={() => {
            navigate(`${ROUTE_PATHS.CHATBOT_WIDGET}?mode=new`);
          }}
        >
          <Icon iconName="whitePlus" size="20x20" className="p-conversationHistory_footer_newBtn_icon" />
          {t('chatbot.newConversation')}
        </button>
      </div>
    </div>
  );
};

export default ConversationHistory;
